<script setup>
import { computed, ref } from 'vue';
import { useStore } from 'vuex';
import api from '../../api';
import { asset, filterProductCategoryBy, getMarketDashboardLink } from '../../helpers';

const props = defineProps({
    modelValue: String
});
const store = useStore();
const products = ref([]);
// NOTE: Get the available products for the customer
// Call immediately on component load
// Setup a base product object for easy HTML rendering
api.get('/api/v1/contentstreams/filters').then(async (response) => {
    for (const [key, value] of Object.entries(response.data.products_without_webinars)) {
        products.value.push({
            id: key,
            name: value,
            link: '',
            readonly: true,
            image: asset('/hkcm/images/logo-small-on-light-background.svg', 'public')
        });
    }

    // Process products in parallel
    const updatedProducts = await Promise.all(
        products.value.map(async (product) => {
            return findOrRequestProductGroup(product);
        })
    );

    products.value = updatedProducts;
});

/**
 * We need the product group to setup the image and the right market-link
 * @param {Object} product
 */
/**
 * Enriches a product with link, image and readonly properties
 * based on its product group. Returns the updated product.
 *
 * @param {Object} product
 * @returns {Promise<Object>}
 */
async function getProductGroup(productId) {
    if (!productId) {
        return null;
    }

    // Try to get from cache first
    const getGroupByProductId = store.getters['product_categories/getGroupByProductId'];

    if (typeof getGroupByProductId === 'function') {
        const cachedGroup = getGroupByProductId(productId);

        if (cachedGroup) {
            return cachedGroup;
        }
    }

    // Fetch from API if not cached
    try {
        const response = await api.get('/api/v1/product-categories/all');
        const groups = response?.data?.data ?? [];

        return filterProductCategoryBy(productId, groups);
    } catch (error) {
        console.error('getProductGroup: failed to load product groups', error);

        return null;
    }
}

async function applyGroupToProduct(product, group) {
    // Return original product if no group or no product id
    if (!group || !product?.id) {
        return product;
    }

    const { productType, products, id, image } = group;

    let link = null;
    let productIds = [];
    let lastContentstreamId = null;

    const isBundle = productType === 'singleProduct' || productType === 'doubleProduct';

    // Build link for bundle products
    if (isBundle) {
        const items = products?.data ?? [];

        productIds = items
            .filter((p) => p?.id && !p?.name?.de?.includes('Video'))
            .map((p) => p.id)
            .filter(Boolean);

        if (productIds.length) {
            const latest = await getLatestContentstreamByProductId(productIds);
            lastContentstreamId = latest?.id;

            if (lastContentstreamId) {
                link = `/dashboard/markets-analysis/${lastContentstreamId}`;
            }
        }
    }

    // Use fallback link if bundle link not available
    if (!link) {
        link = getMarketDashboardLink(productType, id);
    }

    // Create a new object to avoid mutating the original
    return {
        ...product,
        link: link || product.link, // Keep original link if no new one
        image: image ? image.replace('.png', '-gradient.png') : product.image,
        readonly: false
    };
}

async function findOrRequestProductGroup(product) {
    if (!product?.id) {
        return product;
    }

    const group = await getProductGroup(product.id);

    return applyGroupToProduct(product, group);
}
/**
 * Basic helper.
 * Filter the products based on the search input.
 */
const filteredProducts = computed(() => {
    return products.value.filter((product) => {
        return product.name.toLowerCase().includes(props.modelValue.toLowerCase());
    });
});

const makeLatestKey = (productIds = []) => `latestContentstream:${[...productIds].filter(Boolean).sort().join(',')}`;

const getLatestContentstreamByProductId = async (productIds) => {
    try {
        const key = makeLatestKey(productIds);

        // Await the dispatch result since it returns a Promise
        let response = await store.getters['contentstreams/getByKey'](key);

        if (!response?.data) {
            response = await store.dispatch('contentstreams/index', {
                params: {
                    'sort[by]': 'published_at',
                    'sort[order]': 'desc',
                    page_size: 1,
                    page: 1,
                    product_id: productIds,
                    key
                }
            });
        }

        return response?.data?.[0] ?? null;
    } catch (err) {
        console.error('Failed to fetch latest contentstream:', err);

        return null;
    }
};
</script>

<template>
    <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 justify-items-center p-4">
        <div
            v-for="product in filteredProducts"
            :key="product.id"
            class="box h-auto w-full max-w-[300px] justify-self-center group relative fade-in border-0 flex flex-col bg-white rounded-xl shadow-md hover:shadow-lg transition-all"
        >
            <router-link :to="product.link" class="contents">
                <div class="rounded-t-xl flex justify-center bg-neutral-100 duration-300 backdrop-blur-md">
                    <img :src="product.image" :alt="product.name + ' logo'" class="h-20 w-auto m-4 object-contain" />
                </div>

                <div class="flex flex-col items-center justify-center px-4 py-4 text-center">
                    <strong class="text-base font-semibold">
                        {{ product.name }}
                    </strong>
                    <small v-if="product.readonly" class="block text-xs text-gray-500">
                        {{ $t('loading') }}
                    </small>
                    <small v-else class="block text-xs text-gray-500">
                        {{ $t('view_analyses') }}
                    </small>
                </div>
            </router-link>
        </div>
    </div>
</template>
